/**
 * Admin commerce API — gated server-side to professor role.
 *
 * All requests go through the same fetch helper as storefrontApi but
 * with `credentials: 'same-origin'` so the auth cookie is sent.
 */

const API_BASE = '/api/v1/admin';

export class AdminApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'AdminApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j?.detail ?? JSON.stringify(j);
    } catch {
      // ignore
    }
    throw new AdminApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ─── Owners ────────────────────────────────────────────────────────────

export interface OwnerEntry {
  id: number;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  is_reseller: boolean;
  reseller_brand_name: string | null;
  reseller_share_pct: number;
  redemption_count: number;
  gross_paise: number;
  net_paise: number;
  notes: string | null;
}

export interface CreateOwnerRequest {
  name: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  is_reseller?: boolean;
  reseller_brand_name?: string | null;
  reseller_logo_url?: string | null;
  reseller_signature_line?: string | null;
  reseller_share_pct?: number;
}

// ─── Coupons ───────────────────────────────────────────────────────────

export type DiscountType = 'percent' | 'flat' | 'free';
export type CouponScope = 'any' | 'specific';

export interface CouponEntry {
  id: number;
  code: string;
  owner_id: number;
  owner_name: string;
  discount_type: DiscountType;
  discount_value: number;
  scope: CouponScope;
  allowed_report_slugs: string[] | null;
  max_redemptions: number | null;
  redemption_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreateCouponRequest {
  code: string;
  owner_id: number;
  discount_type: DiscountType;
  discount_value: number;
  scope: CouponScope;
  allowed_report_slugs?: string[] | null;
  max_redemptions?: number | null;
  expires_at?: string | null;
}

export interface PatchCouponRequest {
  is_active?: boolean;
  max_redemptions?: number | null;
  expires_at?: string | null;
}

// ─── Orders ────────────────────────────────────────────────────────────

export interface OrderRow {
  id: number;
  public_token: string;
  report_slug: string;
  buyer_email: string;
  buyer_name: string;
  base_price_paise: number;
  discount_paise: number;
  final_price_paise: number;
  final_price_inr_display: string;
  coupon_code: string | null;
  coupon_owner_name: string | null;
  status: string;
  created_at: string;
  paid_at: string | null;
  delivered_at: string | null;
  delivery_attempts: number;
  last_delivery_error: string | null;
}

export interface OrdersResponse {
  total: number;
  rows: OrderRow[];
}

// ─── API surface ───────────────────────────────────────────────────────

export const adminApi = {
  listOwners: () => request<OwnerEntry[]>('/coupon-owners'),
  createOwner: (body: CreateOwnerRequest) =>
    request<OwnerEntry>('/coupon-owners', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listCoupons: () => request<CouponEntry[]>('/coupons'),
  createCoupon: (body: CreateCouponRequest) =>
    request<CouponEntry>('/coupons', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchCoupon: (id: number, body: PatchCouponRequest) =>
    request<CouponEntry>(`/coupons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteCoupon: async (id: number) => {
    const res = await fetch(`${API_BASE}/coupons/${id}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok && res.status !== 204) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail = j?.detail ?? JSON.stringify(j);
      } catch {
        // ignore
      }
      throw new AdminApiError(res.status, detail);
    }
  },

  listOrders: (filters: {
    status?: string;
    report_slug?: string;
    coupon_code?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (filters.status) q.set('status', filters.status);
    if (filters.report_slug) q.set('report_slug', filters.report_slug);
    if (filters.coupon_code) q.set('coupon_code', filters.coupon_code);
    if (filters.limit) q.set('limit', String(filters.limit));
    if (filters.offset) q.set('offset', String(filters.offset));
    const qs = q.toString();
    return request<OrdersResponse>(`/orders${qs ? '?' + qs : ''}`);
  },

  resendOrder: (id: number) =>
    request<{ ok: boolean }>(`/orders/${id}/resend`, { method: 'POST' }),
};
